const ATTEMPT_ID_PREFIX = "exam_attempt_id_";
const ATTEMPT_SNAPSHOT_PREFIX = "exam_attempt_snapshot_";
const RESUME_STATE_PREFIX = "exam_resume_state_";

const uniqueTestKeys = (...testKeys) => [
  ...new Set(
    testKeys
      .filter((value) => value != null && value !== "")
      .map(String)
  ),
];

export const examAttemptSnapshotHasProgress = (snapshot) => {
  const questions = Array.isArray(snapshot?.questions) ? snapshot.questions : [];
  return questions.some((question) => {
    const selected = question?.selectedOption;
    const hasSelectedOption = Array.isArray(selected)
      ? selected.length > 0
      : selected !== null &&
        selected !== undefined &&
        !(typeof selected === "string" && selected === "");
    const hasIntegerAnswer =
      question?.integerAnswer !== null &&
      question?.integerAnswer !== undefined &&
      String(question.integerAnswer).trim() !== "";

    return (
      hasSelectedOption ||
      hasIntegerAnswer ||
      question?.marked === true ||
      Number(question?.question_status) > 0
    );
  });
};

export const beginNewExamAttempt = (...testKeys) => {
  if (typeof window === "undefined") return null;

  const keys = uniqueTestKeys(...testKeys);
  if (!keys.length) return null;

  const attemptId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  keys.forEach((testKey) => {
    window.localStorage.setItem(`${ATTEMPT_ID_PREFIX}${testKey}`, attemptId);
    window.localStorage.removeItem(`${ATTEMPT_SNAPSHOT_PREFIX}${testKey}`);
  });
  return attemptId;
};

export const getCurrentExamAttemptId = (testKey) => {
  if (testKey == null || testKey === "" || typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(`${ATTEMPT_ID_PREFIX}${testKey}`);
};

export const readCurrentExamAttemptSnapshot = (...testKeys) => {
  if (typeof window === "undefined") return null;

  for (const testKey of uniqueTestKeys(...testKeys)) {
    const attemptId = getCurrentExamAttemptId(testKey);
    if (!attemptId) continue;

    try {
      const raw = window.localStorage.getItem(
        `${ATTEMPT_SNAPSHOT_PREFIX}${testKey}`
      );
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      if (parsed?.attemptId !== attemptId) continue;
      if (String(parsed?.testKey) !== String(testKey)) continue;
      return parsed;
    } catch (_) {
      // Ignore malformed attempt snapshots and keep looking.
    }
  }

  return null;
};

export const writeCurrentExamAttemptSnapshot = (snapshot, ...testKeys) => {
  if (typeof window === "undefined" || !snapshot) return;

  const keys = uniqueTestKeys(...testKeys);
  if (!keys.length) return;

  const attemptId = keys.map(getCurrentExamAttemptId).find(Boolean);
  if (!attemptId) return;

  keys.forEach((testKey) => {
    window.localStorage.setItem(`${ATTEMPT_ID_PREFIX}${testKey}`, attemptId);
    window.localStorage.setItem(
      `${ATTEMPT_SNAPSHOT_PREFIX}${testKey}`,
      JSON.stringify({
        ...snapshot,
        testKey,
        attemptId,
        snapshotVersion: 2,
      })
    );
  });
};

export const examAttemptSnapshotIsActive = (snapshot) => {
  if (!snapshot || !Array.isArray(snapshot.questions) || !snapshot.questions.length) {
    return false;
  }
  if (examAttemptSnapshotHasProgress(snapshot)) return true;
  return (
    snapshot.currentQuestionQid !== undefined &&
    snapshot.currentQuestionQid !== null &&
    snapshot.currentQuestionQid !== ""
  );
};

export const readLegacyExamResumeSnapshot = (...testKeys) => {
  if (typeof window === "undefined") return null;

  for (const testKey of uniqueTestKeys(...testKeys)) {
    try {
      const raw = window.localStorage.getItem(`${RESUME_STATE_PREFIX}${testKey}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.snapshotVersion !== 2) continue;
      if (parsed?.testKey && String(parsed.testKey) !== String(testKey)) continue;
      return parsed;
    } catch (_) {
      // Ignore malformed resume snapshots and keep looking.
    }
  }

  return null;
};

export const readInProgressExamSnapshot = (...testKeys) => {
  const attemptSnapshot = readCurrentExamAttemptSnapshot(...testKeys);
  if (examAttemptSnapshotIsActive(attemptSnapshot)) return attemptSnapshot;

  const legacySnapshot = readLegacyExamResumeSnapshot(...testKeys);
  if (examAttemptSnapshotIsActive(legacySnapshot)) return legacySnapshot;

  return attemptSnapshot || legacySnapshot;
};

export const currentExamAttemptHasProgress = (...testKeys) =>
  examAttemptSnapshotHasProgress(readInProgressExamSnapshot(...testKeys));

export const clearExamAttemptSession = (...testKeys) => {
  if (typeof window === "undefined") return;

  uniqueTestKeys(...testKeys).forEach((testKey) => {
    window.localStorage.removeItem(`${ATTEMPT_ID_PREFIX}${testKey}`);
    window.localStorage.removeItem(`${ATTEMPT_SNAPSHOT_PREFIX}${testKey}`);
  });
};
